import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { progressAPI, projectsAPI, catalogAPI } from '../services/api';
import { 
  TrendingUp, 
  Plus, 
  Check,
  X,
  Clock,
  Building2
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';

const ProgressPage = () => {
  const [searchParams] = useSearchParams();
  const { isDeveloper, isConstructor } = useAuth();
  const [loading, setLoading] = useState(true);
  const [progressList, setProgressList] = useState([]);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(searchParams.get('project') || '');
  const [catalog, setCatalog] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    catalog_item_id: '',
    quantity: '',
    notes: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    if (selectedProject) {
      fetchCatalog();
      fetchProgress();
    }
  }, [selectedProject]);

  const fetchData = async () => {
    try {
      const projectsRes = await projectsAPI.getAll();
      const projects = projectsRes?.data?.data || projectsRes?.data || [];
      setProjects(projects);
      if (projects.length > 0 && !selectedProject) {
        setSelectedProject(projects[0].id);
      }
      
      if (isConstructor()) {
        const progressRes = await progressAPI.getMyProgress();
        const progressData = progressRes?.data?.data || progressRes?.data;
        const progress = Array.isArray(progressData) ? progressData : [];
        setProgressList(progress);
      }
    } catch (error) {
      console.error('Error al cargar datos:', error);
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const fetchCatalog = async () => {
    try {
      const response = await catalogAPI.getByProject(selectedProject);
      const catalogData = response?.data?.data || response?.data;
      const catalog = Array.isArray(catalogData) ? catalogData : [];
      setCatalog(catalog);
    } catch (error) {
      console.error('Error fetching catalog:', error);
      setCatalog([]);
    }
  };

  const fetchProgress = async () => {
    try {
      const response = await progressAPI.getByProject(selectedProject);
      const progressData = response?.data?.data || response?.data;
      const progress = Array.isArray(progressData) ? progressData : [];
      setProgressList(progress);
    } catch (error) {
      console.error('Error fetching progress:', error);
      setProgressList([]);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.catalog_item_id || !formData.quantity) {
      toast.error('Selecciona una partida y cantidad');
      return;
    }

    try {
      await progressAPI.create(selectedProject, {
        catalog_item_id: formData.catalog_item_id,
        quantity: parseFloat(formData.quantity),
        notes: formData.notes
      });
      toast.success('Avance registrado');
      setDialogOpen(false);
      setFormData({ catalog_item_id: '', quantity: '', notes: '' });
      fetchProgress();
    } catch (error) {
      toast.error('Error al registrar avance');
    }
  };

  const handleApprove = async (progressId) => {
    try {
      await progressAPI.approve(progressId);
      toast.success('Avance aprobado');
      fetchProgress();
    } catch (error) {
      toast.error('Error al aprobar avance');
    }
  };

  const handleReject = async (progressId) => {
    try {
      await progressAPI.reject(progressId);
      toast.success('Avance rechazado');
      fetchProgress();
    } catch (error) {
      toast.error('Error al rechazar avance');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pendiente: 'bg-amber-100 text-amber-800',
      aprobado: 'bg-green-100 text-green-800',
      rechazado: 'bg-red-100 text-red-800'
    };
    return styles[status] || styles.pendiente;
  };

  const selectedCatalogItem = catalog.find(c => c.id === formData.catalog_item_id);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="space-y-6">
          <Skeleton className="h-8 w-48" />
          <div className="grid gap-4">
            {[1, 2, 3].map((i) => (
              <Skeleton key={i} className="h-24 rounded-xl" />
            ))}
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-6 animate-fade-in" data-testid="progress-page">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-[#2D2A26]">Avances de Obra</h1>
            <p className="text-[#5C554B]">
              {isConstructor() ? 'Captura los avances de tus partidas' : 'Autoriza los volúmenes ejecutados'}
            </p>
          </div>
          {isConstructor() && selectedProject && (
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="btn-primary" data-testid="new-progress-btn">
                  <Plus className="w-5 h-5 mr-2" />
                  Capturar Avance
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Capturar Avance</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 pt-4">
                  <div className="space-y-2">
                    <Label>Partida *</Label>
                    <Select 
                      value={formData.catalog_item_id} 
                      onValueChange={(value) => setFormData({ ...formData, catalog_item_id: value })}
                    >
                      <SelectTrigger className="h-12">
                        <SelectValue placeholder="Seleccionar partida" />
                      </SelectTrigger>
                      <SelectContent>
                        {Array.isArray(catalog) && catalog.map((item) => (
                          <SelectItem key={item.id} value={item.id}>
                            {item.code} - {item.description}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    {selectedCatalogItem && (
                      <p className="text-xs text-[#5C554B]">
                        Disponible: {selectedCatalogItem.quantity - selectedCatalogItem.executed_quantity} {selectedCatalogItem.unit}
                      </p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="quantity">Cantidad ejecutada *</Label>
                    <Input
                      id="quantity"
                      type="number"
                      step="0.01"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      placeholder="0.00"
                      className="input-default"
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="notes">Notas</Label>
                    <Textarea
                      id="notes"
                      value={formData.notes}
                      onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                      placeholder="Observaciones del avance..."
                    />
                  </div>

                  <div className="flex justify-end gap-2 pt-4">
                    <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                      Cancelar
                    </Button>
                    <Button type="submit" className="btn-primary">
                      Registrar
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        {/* Project Selector */}
        <Card className="card-dashboard">
          <CardContent className="pt-6">
            <Label className="mb-2 block">Seleccionar Obra</Label>
            <Select value={selectedProject} onValueChange={setSelectedProject}>
              <SelectTrigger className="h-12 bg-white max-w-md">
                <SelectValue placeholder="Selecciona una obra" />
              </SelectTrigger>
              <SelectContent>
                {Array.isArray(projects) && projects.map((project) => (
                  <SelectItem key={project.id} value={project.id}>
                    {project.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </CardContent>
        </Card>

        {/* Progress List */}
        {!Array.isArray(progressList) || progressList.length === 0 ? (
          <Card className="card-dashboard">
            <CardContent className="py-12 text-center">
              <TrendingUp className="w-16 h-16 text-[#E2E8F0] mx-auto mb-4" />
              <h3 className="text-lg font-semibold text-[#2D2A26] mb-2">No hay avances registrados</h3>
              <p className="text-[#5C554B]">
                {isConstructor() ? 'Captura tu primer avance' : 'Aún no hay avances para autorizar'}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-3">
            {Array.isArray(progressList) && progressList.map((progress) => {
              const catalogItem = catalog.find(c => c.id === progress.catalog_item_id);
              const project = projects.find(p => p.id === progress.project_id);
              
              return (
                <Card key={progress.id} className="card-dashboard">
                  <CardContent className="p-4 md:p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-10 h-10 bg-[#81A183]/10 rounded-lg flex items-center justify-center">
                            <TrendingUp className="w-5 h-5 text-[#81A183]" />
                          </div>
                          <div>
                            <p className="font-semibold text-[#2D2A26]">
                              {catalogItem?.code || 'Partida'} - {progress.quantity} {catalogItem?.unit || 'unidades'}
                            </p>
                            <p className="text-sm text-[#5C554B]">{catalogItem?.description || ''}</p>
                          </div>
                        </div>
                        
                        <div className="flex flex-wrap items-center gap-3 text-sm mt-2">
                          <div className="flex items-center gap-1 text-[#5C554B]">
                            <Building2 className="w-4 h-4" />
                            {project?.name || 'Obra'}
                          </div>
                          <div className="flex items-center gap-1 text-[#5C554B]">
                            <Clock className="w-4 h-4" />
                            {new Date(progress.created_at).toLocaleDateString('es-MX')}
                          </div>
                          <Badge className={getStatusBadge(progress.status)}>
                            {progress.status}
                          </Badge>
                        </div>

                        {progress.notes && (
                          <p className="text-sm text-[#5C554B] mt-2 italic">"{progress.notes}"</p>
                        )}
                      </div>

                      {isDeveloper() && progress.status === 'pendiente' && (
                        <div className="flex gap-2">
                          <Button
                            onClick={() => handleApprove(progress.id)}
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white"
                          >
                            <Check className="w-4 h-4 mr-1" />
                            Aprobar
                          </Button>
                          <Button
                            onClick={() => handleReject(progress.id)}
                            size="sm"
                            variant="outline"
                            className="text-red-600 border-red-200 hover:bg-red-50"
                          >
                            <X className="w-4 h-4 mr-1" />
                            Rechazar
                          </Button>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default ProgressPage;
